import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getCurrentUser, signOut } from 'aws-amplify/auth';
import 'aws-amplify/auth/enable-oauth-listener';

import { fetchUser }  from '../api/profile/fetchUser';
import { fetchUserStats } from '../api/profile/fetchUserStats';
import { fetchVisionLeaderboard } from '../api/fetchVisionLeaderboard';

import SideBar from '../components/SideBar';
import ErrorBoundary from '../components/ErrorBoundary';
import Vision from '../components/Vision';
import ColorGuesser from '../components/ColorGuesser';
import Profile from '../components/Profile';
import Leaderboard from '../components/Leaderboard';
import OpeningPuzzles from '../components/OpeningPuzzles';

function Home() {
    const navigate = useNavigate();
    const [activePage, setActivePage] = useState('vision');
    const [refreshTrigger, setRefreshTrigger] = useState(0);
    const [sidebarCollapsed, setSidebarCollapsed] = useState(window.innerWidth < 768);

    const [authUser, setAuthUser] = useState(null);
    const [authLoading, setAuthLoading] = useState(true);
    const [profileError, setProfileError] = useState(false);
    const [userData, setUserData] = useState(null);
    const [userStats, setUserStats] = useState(null);
    const [visionLeaderboard, setVisionLeaderboard] = useState(null);

    // Check auth on mount — redirect to /login if not signed in
    useEffect(() => {
        let cancelled = false;
        const checkAuth = async () => {
            try {
                const user = await getCurrentUser();
                if (!cancelled) {
                    setAuthUser(user);
                    setAuthLoading(false);
                }
            } catch {
                if (!cancelled) {
                    navigate('/login');
                }
            }
        };
        checkAuth();
        return () => { cancelled = true; };
    }, [navigate]);

    // Fetch user data once auth is confirmed
    useEffect(() => {
        if (!authUser) return;
        const load = async () => {
            try {
                setProfileError(false);
                await Promise.all([
                    fetchUser(authUser.userId, setUserData),
                    fetchVisionLeaderboard(setVisionLeaderboard),
                    fetchUserStats(authUser.userId, setUserStats),
                ]);
            } catch {
                setProfileError(true);
            }
        };
        load();
    }, [authUser, refreshTrigger]);

    const handleSignOut = async () => {
        try {
            await signOut();
            navigate('/login');
        } catch (err) {
            console.error('Sign out error:', err);
        }
    };

    const RenderMainContent = ({ activePage, userData }) => {
        switch (activePage) {
            case 'vision':
                return <ErrorBoundary name="Vision"><Vision userData={userData} /></ErrorBoundary>;
            case 'color guesser':
                return <ErrorBoundary name="Color Guesser"><ColorGuesser userData={userData} /></ErrorBoundary>;
            case 'blindfold puzzles':
                return <ErrorBoundary name="Opening Puzzles"><OpeningPuzzles userData={userData} /></ErrorBoundary>;
            case 'leaderboard':
                return <ErrorBoundary name="Leaderboard"><Leaderboard visionLeaderboard={visionLeaderboard} /></ErrorBoundary>;
            case 'profile':
                return (
                    <ErrorBoundary name="Profile">
                        {profileError && (
                            <div className="alert alert-error m-4">
                                <span>Failed to load profile data.</span>
                                <button className="btn btn-sm btn-ghost" onClick={() => setRefreshTrigger(t => t + 1)}>Retry</button>
                            </div>
                        )}
                        <Profile
                            userData={userData}
                            userStats={userStats}
                            setRefreshTrigger={setRefreshTrigger}
                            onSignOut={handleSignOut}
                        />
                    </ErrorBoundary>
                );
            default:
                return <></>;
        }
    };

    if (authLoading) {
        return (
            <div className="min-h-screen bg-base-100 flex items-center justify-center">
                <span className="loading loading-spinner loading-lg text-primary"></span>
            </div>
        );
    }

    return (
        <div style={{ width: '100%', height: '100%' }}>
            <div className='absolute left-0 top-0 z-10'>
                <SideBar activePage={activePage} setActivePage={setActivePage} onCollapsedChange={setSidebarCollapsed} />
            </div>
            <div className='absolute top-0 h-full overflow-x-hidden' style={{ left: sidebarCollapsed ? 64 : 230, right: 0 }}>
                <RenderMainContent userData={userData} activePage={activePage} />
            </div>
            {activePage !== 'profile' && (
                <div onClick={() => setActivePage('profile')} className='absolute cursor-pointer' style={{ right: '1vw', top: '1vh' }}>
                    <div className="avatar">
                        <div className="rounded-full border-solid border-4" style={{ width: '6vw', maxWidth: 90, minWidth: 60 }}>
                            {userData && <img src={userData.picture} alt="avatar" />}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default Home;
